import Link from 'next/link';
import type { ReactNode } from 'react';

import { useMindCoins } from '@/hooks/useMindCoins';
import { useProfile } from '@/hooks/useProfile';
import { cn } from '@/lib/utils';

import AuthDialog from '../CandidateAuth/AuthDialog';
import { AuthPageEnum } from '../CandidateAuth/AuthEnum';
import Icon from '../Icon';
import Text from '../Text';

const StepBadge = ({
  children,
  variant = 'solid',
}: {
  children: ReactNode;
  variant?: 'solid' | 'outline';
}) => (
  <div
    className={cn(
      'flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
      variant === 'solid'
        ? 'bg-jb-primary-500 text-jb-neutral-100'
        : 'border border-jb-primary-300 text-jb-primary-500'
    )}
  >
    <Text variant="jb-body-xsmall" className="font-semibold">
      {children}
    </Text>
  </div>
);

const TRIGGER_PREVIEW_STEPS = [
  'Choose a resume',
  'Pick how many jobs to scan',
  'Get your match score instantly',
];

const ResumeTriggerContent = ({ footer }: { footer: ReactNode }) => (
  <>
    <div className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-jb-primary-100 px-2.5 py-1">
      <Icon
        icon="material-symbols:bolt-rounded"
        className="text-jb-primary-500"
        fontSize={14}
      />
      <Text
        variant="jb-body-xsmall"
        className="font-semibold text-jb-primary-500"
      >
        AI-powered
      </Text>
    </div>
    <div className="w-fit rounded-full border-2 border-jb-primary-300 bg-jb-neutral-100 p-4 transition-all duration-300 group-hover:scale-105 group-hover:border-jb-primary-400">
      <Icon
        icon="material-symbols:document-scanner"
        className="text-jb-neutral-700"
        fontSize={26}
      />
    </div>
    <Text className="font-semibold text-jb-primary-500" variant="jb-body-large">
      Select your resume
    </Text>

    <div className="flex w-full flex-col items-start gap-1.5 pt-2">
      {TRIGGER_PREVIEW_STEPS.map((step, index) => (
        <div key={step} className="flex items-center gap-2">
          <StepBadge variant="outline">{index + 1}</StepBadge>
          <Text variant="jb-body-small" className="text-jb-neutral-700">
            {step}
          </Text>
        </div>
      ))}
    </div>

    {footer}
  </>
);

const RESUME_TRIGGER_CLASSNAME =
  'group relative flex h-full w-full flex-col items-center justify-center gap-1 overflow-hidden rounded-2xl border border-jb-primary-300 bg-jb-neutral-100 p-6 shadow-big-moduls transition-all hover:border-jb-primary-400 hover:shadow-lg';

const UnauthenticatedResumeCard = () => (
  <AuthDialog
    defaultPage={AuthPageEnum.SIGNUP_PROVIDERS}
    dialogTriggerProps={{ asChild: true }}
  >
    <button type="button" className={RESUME_TRIGGER_CLASSNAME}>
      <ResumeTriggerContent
        footer={
          <div className="flex items-center gap-1 pt-6">
            <Text
              variant="jb-body-medium"
              className="font-semibold text-jb-primary-500"
            >
              Sign in to get started
            </Text>
            <Icon
              icon="material-symbols:arrow-forward-rounded"
              className="text-jb-primary-500 transition-transform group-hover:translate-x-1"
              fontSize={16}
            />
          </div>
        }
      />
    </button>
  </AuthDialog>
);

const AuthenticatedResumeCard = () => {
  const { data: mindCoinsData } = useMindCoins();

  return (
    <Link
      href="/offer-recommendations/new"
      className={RESUME_TRIGGER_CLASSNAME}
    >
      <ResumeTriggerContent
        footer={
          <div className="flex items-center gap-1 pt-6">
            <Text variant="jb-body-medium" className="text-jb-neutral-1000">
              MindCoins left:{' '}
              <span className="font-semibold text-jb-success-400">
                {mindCoinsData?.mindCoins.toLocaleString('en-US') ?? '—'}
              </span>
            </Text>
            <Icon
              icon="material-symbols:arrow-forward-rounded"
              className="text-jb-neutral-700 transition-transform group-hover:translate-x-1"
              fontSize={16}
            />
          </div>
        }
      />
    </Link>
  );
};

const SelectResumeDialog = () => {
  const { data: profileData } = useProfile();
  if (!profileData) return <UnauthenticatedResumeCard />;
  return <AuthenticatedResumeCard />;
};

export default SelectResumeDialog;
